/**
 * Chart API Client
 *
 * Fetches chart data through the prototype same-origin proxy
 * (`/api/tools/charts/...`), which adds the Tools API key server-side.
 * That avoids browser CORS to tools.orbcon.ca.
 */

const API_BASE_URL = '/api/tools/charts';

export interface ApiResponse<T> {
  success: boolean;
  data: T;
  summary?: any;
  meta?: {
    dateRange?: {
      startDate: string;
      endDate: string;
      fiscalYear?: string;
    };
    cached?: boolean;
  };
  error?: string;
}

export interface ChartDataPoint {
  label: string;
  month: number;
  year?: number;
  value: number;
  rbc?: number;
  plasma?: number;
  platelets?: number;
  total?: number;
  transfusedRate?: number;
  discardRate?: number;
  transferredRate?: number;
  redistributedRate?: number;
  [key: string]: any; // Allow additional properties
}

interface ChartApiParams {
  startDate?: string;
  endDate?: string;
  year?: number;
}

async function fetchChartData<T extends ChartDataPoint[]>(
  endpoint: string,
  params?: ChartApiParams
): Promise<ApiResponse<T>> {
  try {
    const queryParams = new URLSearchParams();
    if (params?.startDate) {
      queryParams.append('startDate', params.startDate);
    }
    if (params?.endDate) {
      queryParams.append('endDate', params.endDate);
    }
    if (params?.year) {
      queryParams.append('year', params.year.toString());
    }

    const endpointPath = endpoint.startsWith('/') ? endpoint : `/${endpoint}`;
    const url = `${API_BASE_URL}${endpointPath}${queryParams.toString() ? `?${queryParams.toString()}` : ''}`;

    if (import.meta.env.DEV) {
      console.log(`[API Client] Fetching: ${url}`);
    }

    const response = await fetch(url, {
      method: 'GET',
      headers: {
        Accept: 'application/json',
      },
    });

    if (!response.ok) {
      let errorMessage = `Failed to fetch chart data: ${response.status} ${response.statusText}`;
      try {
        const errorData = await response.json();
        if (errorData.error || errorData.message) {
          errorMessage = errorData.error || errorData.message;
        } else if (typeof errorData === 'string') {
          errorMessage = errorData;
        }
      } catch {
        // If response is not JSON, use default error message
      }

      if (response.status === 401) {
        throw new Error('API authentication failed. Please check your API key.');
      }
      if (response.status === 404) {
        throw new Error('Chart data endpoint not found');
      }
      if (response.status === 400) {
        throw new Error(`Bad Request: ${errorMessage}. Please check your date range and parameters.`);
      }
      if (response.status === 503) {
        throw new Error('Charts API is not configured on the server.');
      }
      throw new Error(errorMessage);
    }

    const data = await response.json();

    if (data.success !== undefined) {
      return data as ApiResponse<T>;
    }

    return {
      success: true,
      data: data as T,
    };
  } catch (error) {
    if (error instanceof Error) {
      throw error;
    }
    throw new Error('Network error occurred while fetching chart data');
  }
}

export const chartApi = {
  /**
   * Get temporal/transfusion trends data
   */
  getTemporalData: (params?: ChartApiParams) =>
    fetchChartData<ChartDataPoint[]>('temporal', params),

  /**
   * Get discard trends data
   */
  getDiscardTrends: (params?: ChartApiParams) =>
    fetchChartData<ChartDataPoint[]>('discard-trends', params),

  /**
   * Get transferred trends data
   */
  getTransferredTrends: (params?: ChartApiParams) =>
    fetchChartData<ChartDataPoint[]>('transferred-trends', params),

  /**
   * Get redistributed trends data
   */
  getRedistributedTrends: (params?: ChartApiParams) =>
    fetchChartData<ChartDataPoint[]>('redistributed-trends', params),
};
